import { useEffect, useRef, useState } from 'react';
import { getContentConfigs, type ConsumerContentConfig } from '@/lib/api';
import { useLocale } from '@/lib/i18n';
import { Button } from '@sabay/ui-kit';

function pickTitle(c: ConsumerContentConfig, locale: string): string {
  if (locale === 'km-KH') return c.titleKm;
  if (locale === 'en') return c.titleEn;
  return c.titleZh;
}

function pickSubtitle(c: ConsumerContentConfig, locale: string): string | null {
  if (locale === 'km-KH') return c.subtitleKm;
  if (locale === 'en') return c.subtitleEn;
  return c.subtitleZh;
}

function openExternal(url: string): void {
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
  const openLink = (tg as unknown as { openLink?: (u: string) => void } | undefined)?.openLink;
  if (typeof openLink === 'function') {
    openLink.call(tg, url);
    return;
  }
  window.location.href = url;
}

/**
 * Koom GO 首页轮播广告位（HOME_CAROUSEL placement）。
 *
 * 视觉规格（DESIGN.md §Components > Carousel Card）：
 *   - 形状：rounded-2xl (20px)
 *   - 单卡宽度：85% 视口，scroll-snap 对齐，露出下一张
 *   - 当 imageUrl 缺省：solid `brand.primary.500` 背景 + `neutral.900` 文字
 *   - 当 imageUrl 存在：图片占满 16:9 区域，文字落在底部 strip
 *   - 底部 dot indicator：active = `brand.primary.700`，idle = `brand.primary.300`
 */
export function HomeCarousel({ cityCode }: { cityCode: string }): React.ReactElement | null {
  const locale = useLocale();
  const [items, setItems] = useState<ConsumerContentConfig[] | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    getContentConfigs({ placement: 'HOME_CAROUSEL', cityCode }, ac.signal)
      .then((res) => setItems(res.items))
      .catch(() => setItems([]));
    return () => ac.abort();
  }, [cityCode]);

  // 单卡宽度计算 = 容器 clientWidth × 0.85；scrollLeft / 单卡宽度 ≈ activeIndex
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !items || items.length === 0) return;
    const onScroll = (): void => {
      const cardWidth = el.clientWidth * 0.85 + 12; // 12px gap-3
      const next = Math.round(el.scrollLeft / cardWidth);
      if (next !== activeIndex) setActiveIndex(Math.min(next, items.length - 1));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [items, activeIndex]);

  if (!items || items.length === 0) return null;

  return (
    <section aria-label="carousel" className="space-y-2">
      <div
        ref={scrollRef}
        className="-mx-5 overflow-x-auto scroll-smooth snap-x snap-mandatory no-scrollbar"
      >
        <div className="flex gap-3 px-5 pb-1">
          {items.map((c) => (
            <CarouselCard
              key={c.id}
              item={c}
              locale={locale}
              onTap={() => openExternal(c.targetUrl)}
            />
          ))}
        </div>
      </div>

      {items.length > 1 ? (
        <div
          className="flex justify-center items-center gap-1.5"
          role="tablist"
          aria-label="carousel pagination"
        >
          {items.map((c, i) => {
            const isActive = i === activeIndex;
            return (
              <span
                key={c.id}
                role="tab"
                aria-selected={isActive}
                className={[
                  'h-1 rounded-full transition-all duration-fast',
                  isActive
                    ? 'w-5 bg-brand-primary-700'
                    : 'w-1.5 bg-brand-primary-300',
                ].join(' ')}
              />
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

interface CarouselCardProps {
  item: ConsumerContentConfig;
  locale: string;
  onTap: () => void;
}

function CarouselCard({ item, locale, onTap }: CarouselCardProps): React.ReactElement {
  const title = pickTitle(item, locale);
  const subtitle = pickSubtitle(item, locale);
  const hasImage = Boolean(item.imageUrl);

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onTap}
      className={[
        'w-[85%] flex-shrink-0 snap-start text-left transition active:scale-[0.985]',
        'rounded-2xl overflow-hidden shadow-sm',
        hasImage ? 'bg-neutral-100' : 'bg-brand-primary-500',
      ].join(' ')}
    >
      {hasImage ? (
        <div
          className="aspect-[16/9] w-full bg-center bg-cover"
          style={{ backgroundImage: `url(${item.imageUrl})` }}
          role="img"
          aria-label={title}
        />
      ) : (
        <div className="aspect-[16/9] w-full grid place-items-center px-4 py-3">
          <h3 className="text-neutral-900 font-bold text-lg leading-tight text-center">
            {title}
          </h3>
        </div>
      )}
      {hasImage ? (
        <div className="px-4 py-3 space-y-1 bg-neutral-0">
          <h3 className="text-base font-semibold text-neutral-900 truncate">
            {title}
          </h3>
          {subtitle ? (
            <p className="text-xs text-neutral-500 truncate">{subtitle}</p>
          ) : null}
        </div>
      ) : null}
    </Button>
  );
}
